/*
AI events from the radio calendar, shared by the home block (eventsHome.js) and the
/calendar/events/ page (eventsPage.js). The radio serves only events a moderator approved, with
https links only. `date` is the event's own calendar day (YYYY-MM-DD); for a rumour that
names only the month `date_precision` is "month" and `date` is its first day.
`date_status` says whether the organizer confirmed it
("confirmed"), it is a rumour ("rumored", with date_source_url) or the rumoured day passed
without confirmation ("unconfirmed_passed"); starts_at is UTC or null.
Everything here is plain data work — the callers build the DOM.
*/

const CALENDAR_URL = 'https://radio.thehype.news/event-calendar';
const FETCH_TIMEOUT_MS = 5000;
const LIVE_WINDOW_MS = 3 * 3600 * 1000;
const PAST_AFTER_MS = 24 * 3600 * 1000;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_PATTERN = /^\d{4}-\d{2}$/;

export const RADIO_URL = 'https://radio.thehype.news/';

/**
 * Loads the approved events, oldest first.
 * @returns {Promise<Array<Object>>} events with at least a title and a YYYY-MM-DD date
 * @throws {Error} when the radio does not answer in time or answers with an error
 */
export async function fetchEvents() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
        const response = await fetch(CALENDAR_URL, { signal: controller.signal });
        if (!response.ok) {
            throw new Error(`event calendar answered ${response.status}`);
        }
        const calendar = await response.json();
        const events = Array.isArray(calendar.events) ? calendar.events : [];
        return events.filter(isUsableEvent);
    } finally {
        clearTimeout(timer);
    }
}

function isUsableEvent(event) {
    return Boolean(event)
        && typeof event.title === 'string'
        && typeof event.date === 'string'
        && DAY_PATTERN.test(event.date);
}

/** The event has a known day, not only a rumoured month. */
export function hasExactDay(event) {
    return event.date_precision !== 'month';
}

/** Calendar key of the event: its day (YYYY-MM-DD) or, for a rumoured month, YYYY-MM. */
export function dayKeyOf(event) {
    return hasExactDay(event) ? event.date : event.date.slice(0, 7);
}

/** The date is a rumour, not announced by the organizer. */
export function isRumored(event) {
    return event.date_status === 'rumored';
}

/** The rumoured day is gone and nobody confirmed the event happened. */
export function isUnconfirmedPassed(event) {
    return event.date_status === 'unconfirmed_passed';
}

/** YYYY-MM-DD of a Date in the visitor's time zone — same shape as event.date. */
export function dateKey(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

/** Local midnight of a YYYY-MM-DD key, for calendar arithmetic. */
export function dateFromKey(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/** Events grouped by dayKeyOf(), in the order they came. */
export function groupByDay(events) {
    const days = new Map();
    events.forEach((event) => {
        const key = dayKeyOf(event);
        if (!days.has(key)) {
            days.set(key, []);
        }
        days.get(key).push(event);
    });
    return days;
}

function startTime(event) {
    if (!event.starts_at || event.time_tba) {
        return null;
    }
    const time = Date.parse(event.starts_at);
    return Number.isNaN(time) ? null : time;
}

/** The organizer confirmed the date (events published before date statuses count as confirmed). */
export function isConfirmed(event) {
    return !event.date_status || event.date_status === 'confirmed';
}

/**
 * True from the announced start for three hours. Only a confirmed day can be live — a
 * rumoured time keeps its rumour mark; events without a time are never live.
 */
export function isLive(event, now) {
    const start = startTime(event);
    return isConfirmed(event)
        && hasExactDay(event)
        && start !== null
        && start <= now.getTime()
        && now.getTime() < start + LIVE_WINDOW_MS;
}

/**
 * Over: the radio says so (it counts a day after the start, in UTC), or a day has passed
 * since the announced start. The visitor's calendar day is not used — in Tokyo a US
 * event would turn past hours before it begins. A passed rumour is not "over".
 */
export function isOver(event, now) {
    if (isUnconfirmedPassed(event)) {
        return false;
    }
    if (event.status === 'past') {
        return true;
    }
    const start = startTime(event);
    return start !== null && now.getTime() > start + PAST_AFTER_MS;
}

/**
 * Start time in the visitor's zone, e.g. "19:00 GMT+3", or "Time TBA". When the local day
 * differs from the event's day the weekday is added, e.g. "Wed 02:00 GMT+9".
 */
export function formatStart(event) {
    const start = startTime(event);
    if (start === null) {
        return 'Time TBA';
    }
    const date = new Date(start);
    const time = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });
    if (dateKey(date) === event.date) {
        return time;
    }
    return `${date.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`;
}

/** Local start as "16:27" for tight spots (zone shown on /calendar/events/); "" if no time. */
export function formatClock(event) {
    const start = startTime(event);
    if (start === null) {
        return '';
    }
    return new Date(start).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** "Tue · Sep 29" for a YYYY-MM-DD key, "November 2026" for a YYYY-MM one; CSS sets the case. */
export function formatDay(key) {
    if (MONTH_PATTERN.test(key)) {
        return dateFromKey(`${key}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    const date = dateFromKey(key);
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${weekday} · ${month} ${date.getDate()}`;
}

/** Stable anchor id of an event card on the /calendar/events/ page. */
export function eventAnchor(event) {
    const slug = event.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return `event-${event.date}-${slug}`;
}

/** Anchor id of a day group on the /calendar/events/ page. */
export function dayAnchor(key) {
    return `day-${key}`;
}

/** The link only when it is https — the radio already filters, the page double-checks. */
export function safeUrl(url) {
    return typeof url === 'string' && url.startsWith('https://') ? url : null;
}

/** Element with a class and optional text — the only way these modules put text on screen. */
export function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) {
        node.className = className;
    }
    if (text !== undefined) {
        node.textContent = text;
    }
    return node;
}
